import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { LogEntry } from '@/lib/server/research'

import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Media } from '@/components/generals/media'
import { Reveal } from '@/components/generals/reveal'
import { ArcDivider, RingsDivider } from '@/components/generals/hairline'
import { ResearchHero } from '@/components/heroes/research-hero'
import { FieldChart } from '@/components/research/field-chart'
import { DriftFigure, EclipseFigure } from '@/components/research/figures'
import { getLogEntries } from '@/lib/server/research'
import { formatKey } from '@/lib/time'

type Props = {
    entries: LogEntry[]
    failed: boolean
}

const MarginNote = ({ children }: { children: ReactNode }): ReactNode => (
    <aside className='label-mono border-l border-brass/50 pl-4 text-[0.65rem] normal-case leading-relaxed tracking-[0.04em] text-star-3 xl:absolute xl:-left-64 xl:w-52 xl:border-l-0 xl:border-r xl:pl-0 xl:pr-4 xl:text-right'>
        {children}
    </aside>
)

const Research: FC<Props> = ({ entries, failed }): ReactNode => {
    const [type, setType] = useState('all')
    const [year, setYear] = useState('all')

    const types = useMemo(() => Array.from(new Set(entries.map(entry => entry.objectType))).sort(), [entries])
    const years = useMemo(() => Array.from(new Set(entries.map(entry => entry.observedOn.slice(0, 4)))).sort().reverse(), [entries])
    const shown = entries.filter(entry => (type === 'all' || entry.objectType === type) && (year === 'all' || entry.observedOn.startsWith(year)))

    return (
        <Layout>
            <Seo
                title='Research'
                description='The CosmicLens observatory logbook: timings of eclipsing stars, exoplanet transits and variable stars from our 40 cm reflector, and the story of our current research programme.'
                ogImage='/images/sky/m13-hercules.jpg'
            />

            <ResearchHero entry={entries[0] ?? null} contentId='logbook' />

            <section className='container-page pb-14 pt-10 md:pt-14'>
                <Reveal variant='wipe' className='max-w-2xl'>
                    <p className='text-lg text-star-2'>
                        Every clear night that the big telescope is not showing visitors the sky, it is measuring something. These are the entries from the log, written up the morning after by whoever was on the night shift.
                    </p>
                </Reveal>
            </section>

            <section id='logbook' aria-labelledby='log-title' className='container-page scroll-mt-20 pb-24 outline-none'>
                <h2 id='log-title' className='sr-only'>
                    Log entries
                </h2>
                <div className='flex flex-wrap items-end gap-6 border-y border-hair py-6'>
                    <div className='min-w-[13rem]'>
                        <label htmlFor='log-type' className='label-mono text-star-3'>
                            Object type
                        </label>
                        <select id='log-type' value={type} onChange={event => setType(event.target.value)} className='field mt-1 text-base'>
                            <option value='all'>
                                Every kind
                            </option>
                            {types.map(item => (
                                <option key={item} value={item}>
                                    {item}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className='min-w-[9rem]'>
                        <label htmlFor='log-year' className='label-mono text-star-3'>
                            Year
                        </label>
                        <select id='log-year' value={year} onChange={event => setYear(event.target.value)} className='field mt-1 text-base'>
                            <option value='all'>
                                Every year
                            </option>
                            {years.map(item => (
                                <option key={item} value={item}>
                                    {item}
                                </option>
                            ))}
                        </select>
                    </div>
                    <p className='label-mono ml-auto text-star-3' aria-live='polite'>
                        {`${shown.length} entr${shown.length === 1 ? 'y' : 'ies'}`}
                    </p>
                </div>

                {failed && (
                    <p className='mt-10 text-star-2'>
                        The logbook could not be opened just now. Please refresh in a moment.
                    </p>
                )}

                <ol>
                    {shown.map(entry => (
                        <li key={entry.id} className='grid gap-6 border-b border-hair py-8 md:grid-cols-[6rem_11rem_1fr] md:gap-10'>
                            <div className='w-20 md:w-24'>
                                <FieldChart ra={entry.ra} dec={entry.dec} label={`Finder chart around ${entry.target}`} />
                            </div>
                            <div>
                                <p className='font-heading text-2xl italic text-star'>
                                    {formatKey(entry.observedOn, 'short')}
                                </p>
                                <p className='data-mono mt-1 text-star-3'>
                                    {entry.observedOn.slice(0, 4)}
                                </p>
                            </div>
                            <article>
                                <p className='label-mono text-brass'>
                                    {entry.objectType}
                                </p>
                                <h3 className='mt-2 font-heading text-3xl text-star'>
                                    {entry.target}
                                </h3>
                                <dl className='data-mono mt-3 flex flex-wrap gap-x-6 gap-y-1 text-[0.8rem] text-star-3'>
                                    <div className='flex gap-2'>
                                        <dt>Instrument</dt>
                                        <dd className='text-star-2'>{entry.instrument}</dd>
                                    </div>
                                    {entry.exposure && (
                                        <div className='flex gap-2'>
                                            <dt>Exposure</dt>
                                            <dd className='text-star-2'>{entry.exposure}</dd>
                                        </div>
                                    )}
                                </dl>
                                <p className='mt-4 max-w-2xl text-star-2'>
                                    {entry.note}
                                </p>
                                <p className='label-mono mt-4 text-[0.6rem] text-star-3'>
                                    {entry.observer}
                                </p>
                            </article>
                        </li>
                    ))}
                    {!failed && shown.length === 0 && (
                        <li className='py-10 text-star-2'>
                            No entries match. Try another kind of object or year.
                        </li>
                    )}
                </ol>
            </section>

            <div className='container-page'>
                <ArcDivider label='Current programme' />
            </div>

            <article aria-labelledby='feature-title' className='pb-32 pt-24'>
                <header className='container-page'>
                    <p className='label-mono text-brass'>
                        Feature · The current programme
                    </p>
                    <h2 id='feature-title' className='display-serif mt-5 max-w-5xl text-6xl text-star md:text-8xl'>
                        The quiet variables
                    </h2>
                    <p className='mt-8 max-w-3xl font-heading text-2xl font-light leading-snug text-star-2 md:text-3xl'>
                        Why a small observatory on Exmoor spends its best nights watching the same few stars dim and brighten, again and again.
                    </p>
                    <p className='label-mono mt-8 text-star-3'>
                        Dr Imogen Hale, Head of Research · October 2026
                    </p>
                </header>

                <Reveal className='mt-16'>
                    <Media src='/images/sky/m13-hercules.jpg' alt='The Great Hercules Cluster, hundreds of thousands of stars, photographed by Hubble' sizes='100vw' className='aspect-[21/9] w-full'>
                        <span className='label-mono absolute bottom-4 right-4 z-[2] text-[0.6rem] text-star-2'>
                            M13 · NASA, ESA and the Hubble Heritage Team
                        </span>
                    </Media>
                </Reveal>

                <div className='container-page mt-20'>
                    <div className='prose-observatory relative mx-auto max-w-2xl text-[1.0625rem] leading-[1.8] xl:ml-[calc(16rem+8%)]'>
                        <p className='font-heading text-2xl font-light leading-relaxed text-star'>
                            Most of the stars you can see never change. A few do, and they are some of the most useful objects in the sky.
                        </p>

                        <div className='relative'>
                            <MarginNote>
                                An eclipsing binary is two stars orbiting so nearly edge-on to us that one passes in front of the other every orbit.
                            </MarginNote>
                            <p>
                                Algol, in Perseus, is the most famous. Every 2.87 days its fainter companion slides across the brighter star and, for about ten hours, Algol loses two thirds of its light. The Arabic name means the demon&apos;s head, and it is not hard to see why people found it unsettling.
                            </p>
                        </div>

                        <p>
                            If you time those eclipses precisely, minute by minute, year after year, you find they do not arrive exactly when the textbook says. Sometimes they are early, sometimes late. The difference is tiny, a few minutes over years, but it is real, and it carries information: about a third star tugging the pair back and forth, about mass flowing from one star to the other, about how the orbit itself is slowly changing.
                        </p>

                        <div className='my-12 xl:-mr-28 2xl:-mr-48'>
                            <EclipseFigure />
                        </div>

                        <h3>
                            A small telescope with a long memory
                        </h3>

                        <div className='relative'>
                            <MarginNote>
                                Our 40 cm reflector times a minimum to within about thirty seconds. A professional telescope does not do much better, because the limit is the star, not the instrument.
                            </MarginNote>
                            <p>
                                Large observatories are booked months ahead and cannot watch one star every few nights for a decade. We can. Since 2019 we have timed thirty-four eclipses of Algol alone, along with a dozen other eclipsing pairs, and sent every result to the variable star sections of the British Astronomical Association and to the international archives that professional astronomers draw on.
                            </p>
                        </div>

                        <p>
                            The pattern in our own timings is now clear enough to see by eye. Algol&apos;s eclipses are drifting later, about half a minute a year, in line with the slow pull of its third star. It is a small result. It is also exactly the kind of long, patient measurement that only places like this one have the time to make.
                        </p>

                        <div className='my-12 xl:-mr-28 2xl:-mr-48'>
                            <DriftFigure />
                        </div>

                        <h3>
                            Planets around other suns
                        </h3>

                        <div className='relative'>
                            <MarginNote>
                                ExoClock is a project that keeps track of when exoplanets will cross their stars, ready for the European Space Agency&apos;s Ariel mission.
                            </MarginNote>
                            <p>
                                The same technique finds planets. When a planet passes in front of its star, the star dims by a per cent or two for a few hours. We cannot discover new worlds this way with a telescope our size, but we can time the ones already known, and that matters: a space telescope that wants to study a planet&apos;s atmosphere has to know, to the minute, when it will be in front of its star.
                            </p>
                        </div>

                        <p>
                            One of our regular targets, WASP-12 b, is falling slowly into its star. Each transit arrives a fraction of a second earlier than the last. Our timings join hundreds of others from around the world that are measuring how fast it is happening.
                        </p>

                        <blockquote className='my-14 border-l border-brass pl-6 font-heading text-3xl font-light leading-snug text-star xl:-ml-24'>
                            The universe is mostly patient. The useful thing is to be patient with it.
                        </blockquote>

                        <h3>
                            Come and help
                        </h3>
                        <p>
                            On the first Thursday of every month the research team opens the control room to members. You can watch a night&apos;s observations come in, learn to estimate a variable star&apos;s brightness by eye, and take a share of the results home. No experience needed; patience helps.
                        </p>
                    </div>
                </div>

                <div className='container-page mt-20 grid gap-6 md:grid-cols-2'>
                    <Media src='/images/telescope-deck.jpg' alt='The roof deck and its telescopes at night' sizes='(min-width: 768px) 50vw, 100vw' className='aspect-[4/5] w-full' />
                    <div className='grid gap-6'>
                        <Media src='/images/brass-instruments.jpg' alt='Brass instruments from the observatory collection' sizes='(min-width: 768px) 50vw, 100vw' className='aspect-[4/3] w-full' />
                        <Media src='/images/sky/m31-andromeda-strip.jpg' alt='Part of the Andromeda Galaxy, photographed by Hubble' sizes='(min-width: 768px) 50vw, 100vw' className={cn('aspect-[3/1] w-full')} />
                    </div>
                </div>

                <div className='container-page'>
                    <RingsDivider className='mt-24' label='Results sent to the BAA Variable Star Section and ExoClock' />
                </div>
            </article>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<Props> = async () => {
    try {
        const entries = await getLogEntries(200)

        return { props: { entries, failed: false } }
    }
    catch (error) {
        console.error('Research log could not load:', error instanceof Error ? error.message : error)

        return { props: { entries: [], failed: true } }
    }
}

export default Research
